import React, { useEffect, useRef, useState } from 'react';
import { Button } from './button';
import Icon from '../ui/icon';

interface VoiceRecordingModalProps {
  isOpen: boolean;
  onClose: () => void;
  isRecording: boolean;
  isRequestingPermission: boolean;
  currentTranscript: string;
  interimTranscript: string;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onCancel: () => void;
  onSendMessage: () => void;
  userName?: string;
  error?: string | null;
}

export const VoiceRecordingModal: React.FC<VoiceRecordingModalProps> = ({
  isOpen,
  onClose,
  isRecording,
  isRequestingPermission,
  currentTranscript,
  interimTranscript,
  onStartRecording,
  onStopRecording,
  onCancel,
  onSendMessage,
  userName = 'Bạn',
  error
}) => {
  const hasTranscript = currentTranscript || interimTranscript;
  const transcriptRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  
  // UI state for smooth transitions
  const [uiIsRecording, setUiIsRecording] = useState(false);
  const [uiIsRequestingPermission, setUiIsRequestingPermission] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [showSendButton, setShowSendButton] = useState(false);

  // Auto scroll to bottom when transcript updates
  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [currentTranscript, interimTranscript]);

  // Smooth UI transitions
  useEffect(() => {
    if (isRequestingPermission) {
      setUiIsRequestingPermission(true);
      setUiIsRecording(false);
      setIsStopping(false);
    } else if (isRecording) {
      // Delay UI change to prevent jarring transition
      const timer = setTimeout(() => {
        setUiIsRequestingPermission(false);
        setUiIsRecording(true);
        setIsStopping(false);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      // When stopping, show stopping state briefly before showing final state
      if (uiIsRecording) {
        setIsStopping(true);
        const timer = setTimeout(() => {
          setUiIsRequestingPermission(false);
          // KHÔNG reset uiIsRecording về false nếu có transcript
          // Chỉ reset nếu không có transcript
          if (!hasTranscript) {
            setUiIsRecording(false);
          }
          setIsStopping(false);
        }, 150); // Show stopping state for 150ms
        return () => clearTimeout(timer);
      } else {
        setUiIsRequestingPermission(false);
        // Chỉ reset uiIsRecording nếu không có transcript
        if (!hasTranscript) {
          setUiIsRecording(false);
        }
        setIsStopping(false);
      }
    }
  }, [isRequestingPermission, isRecording, uiIsRecording, hasTranscript]);

  // Handle transcript changes without affecting UI state
  useEffect(() => {
    // Khi có transcript, đảm bảo UI ở trạng thái đúng
    // Nhưng không làm refresh nút send
    if (hasTranscript && !isRecording && !isRequestingPermission) {
      // Chỉ đảm bảo UI state đúng, không thay đổi gì khác
      setUiIsRequestingPermission(false);
      setIsStopping(false);
      // Set showSendButton một lần duy nhất khi có transcript
      if (!showSendButton) {
        setShowSendButton(true);
      }
    } else if (!hasTranscript && !isRecording && !isRequestingPermission) {
      // Reset showSendButton khi không có transcript
      setShowSendButton(false);
    }
  }, [hasTranscript, isRecording, isRequestingPermission, showSendButton]);

  // Reset showSendButton when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowSendButton(false);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Removed unused handleCancel function

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        ref={modalRef}
        className="bg-background rounded-2xl w-full max-w-md max-h-[90vh] flex flex-col shadow-2xl border-2 border-secondary"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">
            {uiIsRequestingPermission 
                ? 'Đang xin quyền microphone...'
                : isStopping
                    ? 'Đang xử lý...'
                    : uiIsRecording 
                        ? `Ngọc đang nghe...`
                        : hasTranscript
                            ? `Đã ghi âm xong!`
                            : `Bấm micro để nói yêu cầu nhé!`
                }
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <Icon name="x" size={20} />
          </Button>
        </div>

        {/* Main Content */}
        <div className="flex flex-col p-6 flex-1">
          {/* Error Display */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2">
                <Icon name="alert-circle" size={20} className="text-red-500" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Transcript Area */}
          <div className="flex-1 mb-6">
            <div className="bg-muted rounded-xl p-4 border-2 border-secondary min-h-[200px] max-h-[300px]">
              <div 
                ref={transcriptRef}
                className="h-full overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
              >
                <div className="text-foreground text-lg leading-relaxed">
                  {currentTranscript}
                  <span className="text-blue-500 italic">
                    {interimTranscript}
                  </span>
                  {!hasTranscript && !error && !uiIsRequestingPermission && (
                    <span className="text-muted-foreground italic">
                      {userName} nói yêu cầu nhé!
                    </span>
                  )}
                  {uiIsRequestingPermission && (
                    <div className="text-center">
                      <div className="text-muted-foreground mb-2">
                        {userName} vui lòng cho phép quyền truy cập microphone ở cửa sổ của trình duyệt nhé!
                      </div>
                      <div className="text-sm text-blue-600">
                        Sau khi cho phép, bạn có thể nói ngay!
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Microphone Button */}
          <div className="flex flex-col items-center mb-6">
            <Button
              onClick={showSendButton ? onSendMessage : (uiIsRecording ? onStopRecording : onStartRecording)}
              disabled={uiIsRequestingPermission || isStopping}
              className={`w-20 h-20 rounded-full shadow-lg transition-all duration-300 hover:scale-105 ${
                uiIsRequestingPermission || isStopping
                  ? 'bg-gray-400 cursor-not-allowed'
                  : showSendButton
                    ? 'bg-green-500 hover:bg-green-600'
                    : uiIsRecording
                      ? 'bg-red-500 hover:bg-red-600'
                      : 'bg-blue-500 hover:bg-blue-600'
              }`}
            >
              {uiIsRequestingPermission ? (
                <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : isStopping ? (
                <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : showSendButton ? (
                <Icon name="send" size={32} className="text-white" />
              ) : uiIsRecording ? (
                <div className="w-8 h-8 bg-white rounded-sm animate-pulse" />
              ) : (
                <Icon name="mic" size={32} className="text-white" />
              )}
            </Button>

            {/* Status Text */}
            <p className="text-sm text-muted-foreground text-center mt-3 max-w-xs">
              {uiIsRequestingPermission 
                ? 'Đang chờ bạn cho phép quyền microphone...'
                : isStopping
                  ? 'Đang xử lý giọng nói...'
                  : showSendButton
                    ? 'Bấm để gửi yêu cầu hoặc ghi âm lại'
                    : uiIsRecording 
                      ? 'Bấm để dừng ghi âm'
                      : 'Bấm để bắt đầu nói'
              }
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            {showSendButton ? (
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={onStartRecording}
                  className="flex-1 h-12 gap-2"
                >
                  <Icon name="mic" size={20} />
                  Ghi âm lại
                </Button>
                <Button
                  variant="outline"
                  onClick={onCancel}
                  className="flex-1 h-12 gap-2"
                >
                  <Icon name="x" size={20} />
                  Hủy
                </Button>
              </div>
            ) : (
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={onCancel}
                  disabled={!hasTranscript}
                  className="flex-1 h-12 gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="x" size={20} />
                  Hủy ghi âm
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
